'use client';
import { LDProvider, useFlags, useLDClient } from 'launchdarkly-react-client-sdk';
import { createContext, useContext } from 'react';

const LaunchDarklyContext = createContext<LaunchDarklyState>({} as LaunchDarklyState);

export const useLaunchDarkly = () => useContext(LaunchDarklyContext);

export function LaunchDarklyProvider({ children }: LaunchDarklyProviderProps) {
    if (!process.env.NEXT_PUBLIC_LD_CLIENT_SIDE_ID) {
        return <LaunchDarklyWrappedProvider>{children}</LaunchDarklyWrappedProvider>;
    }

    return (
        <LDProvider
            clientSideID={process.env.NEXT_PUBLIC_LD_CLIENT_SIDE_ID}
            context={{
                anonymous: true,
                kind: 'user',
            }}
            reactOptions={{
                useCamelCaseFlagKeys: false,
            }}
        >
            <LaunchDarklyWrappedProvider enabled>{children}</LaunchDarklyWrappedProvider>
        </LDProvider>
    );
}

function LaunchDarklyWrappedProvider({ children, enabled }: LaunchDarklyWrappedProviderProps) {
    const client = useLDClient();
    const flags = useFlags();

    function render(flag: string, variations: LaunchDarklyMap) {
        // Check client
        if (!client && enabled) {
            return null;
        }

        // Get variant
        const variant = client?.variation(flag);
        return variations[variant] || variations['default'] || Object.values(variations)[0] || null;
    }

    const state = {
        flags,
        render,
    };

    return <LaunchDarklyContext.Provider value={state}>{children}</LaunchDarklyContext.Provider>;
}
